import api, { apiWithPrefix } from '../utils/api';

/**
 * Authentication Service
 * Handles all authentication-related API calls
 */
export class AuthService {
  /**
   * Google OAuth login
   */
  static async googleLogin(code) {
    try {
      const response = await api.get(`/auth/google?code=${code}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Google login failed');
    }
  }

  /**
   * Verify MFA with OTP
   */
  static async verifyMFA(email, otp) {
    try {
      const response = await api.post('/auth/verify-mfa', { email, otp });
      
      // Store tokens in localStorage
      if (response.data.accessToken) {
        const userInfo = {
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
          user: response.data.user
        };
        localStorage.setItem('user-info', JSON.stringify(userInfo));
      }
      
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'MFA verification failed');
    }
  }

  /**
   * Refresh access token
   */
  static async refreshToken(refreshToken) {
    try {
      const response = await api.post('/auth/token', { refreshToken });
      
      // Update stored tokens
      const userInfo = JSON.parse(localStorage.getItem('user-info') || '{}');
      userInfo.accessToken = response.data.accessToken;
      localStorage.setItem('user-info', JSON.stringify(userInfo));
      
      return response.data;
    } catch (error) {
      // Clear invalid tokens
      localStorage.removeItem('user-info');
      throw new Error(error.response?.data?.message || 'Token refresh failed');
    }
  }

  /**
   * Logout user
   */
  static async logout() {
    try {
      const userInfo = JSON.parse(localStorage.getItem('user-info') || '{}');
      
      if (userInfo.refreshToken) {
        await api.post('/auth/logout', { refreshToken: userInfo.refreshToken });
      }
      
      // Clear local storage
      localStorage.removeItem('user-info');
      
      return { success: true };
    } catch (error) {
      // Clear local storage even if API call fails
      localStorage.removeItem('user-info');
      throw new Error(error.response?.data?.message || 'Logout failed');
    }
  }

  /**
   * Change password
   */
  static async changePassword(currentPassword, newPassword) {
    try {
      const response = await api.post('/auth/change-password', {
        currentPassword,
        newPassword
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Password change failed');
    }
  }

  /**
   * Get current user from localStorage
   */
  static getCurrentUser() {
    try {
      const userInfo = localStorage.getItem('user-info');
      return userInfo ? JSON.parse(userInfo) : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  static isAuthenticated() {
    const userInfo = this.getCurrentUser();
    return !!(userInfo && userInfo.accessToken);
  }

  /**
   * Get user role
   */
  static getUserRole() {
    const userInfo = this.getCurrentUser();
    return userInfo?.user?.role || null;
  }

  /**
   * Check if user has specific role (case-insensitive)
   */
  static hasRole(role) {
    const userRole = this.getUserRole();
    return userRole?.toLowerCase() === role.toLowerCase();
  }

  /**
   * Check if user has admin privileges
   */
  static isAdmin() {
    const userRole = this.getUserRole();
    return userRole?.toLowerCase() === 'admin';
  }

  /**
   * Check if user has HR privileges
   */
  static isHR() {
    const userRole = this.getUserRole();
    return userRole?.toLowerCase() === 'hr';
  }

  /**
   * Check if user has manager privileges
   */
  static isManager() {
    const userRole = this.getUserRole();
    return userRole?.toLowerCase() === 'manager';
  }

  static isTeamLead() {
    const userRole = this.getUserRole();
    return userRole?.toLowerCase() === 'team_lead';
  }

  static async getDevUsers() {
    const response = await apiWithPrefix.get('/dev/auth/users');
    return response.data;
  }

  static async loginAsDevUser(email, role) {
    const response = await apiWithPrefix.post('/dev/auth/login', { email, role });
    return response.data;
  }

  static async switchDevRole(role) {
    const response = await apiWithPrefix.post('/dev/auth/switch-role', { role });
    return response.data;
  }
}

export default AuthService;
